import React, { useState } from "react";
import { MdClose, MdErrorOutline } from "react-icons/md";

/** "@Acme.com " -> "acme.com". Vazio depois de normalizar = descartar. */
export const normalizarDominio = (valor: string) =>
  valor.trim().toLowerCase().replace(/^@+/, "");

type Props = {
  id: string;
  value: string[];
  onChange: (dominios: string[]) => void;
  disabled?: boolean;
  /** Mensagem do backend (409 de domínio repetido), mostrada embaixo do campo. */
  error?: string;
  labelCls: string;
  inputCls: string;
};

/** Domínios de e-mail da organização em chips: Enter adiciona, × remove, sempre em minúsculas. */
const DominiosField: React.FC<Props> = ({ id, value, onChange, disabled, error, labelCls, inputCls }) => {
  const [rascunho, setRascunho] = useState("");

  const adicionar = () => {
    // Aceita vários de uma vez ("a.com, b.com") para colar uma lista.
    const novos = rascunho
      .split(/[\s,;]+/)
      .map(normalizarDominio)
      .filter((d) => d && !value.includes(d));
    if (novos.length) onChange([...value, ...Array.from(new Set(novos))]);
    setRascunho("");
  };

  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <label htmlFor={id} className={labelCls}>
        Domínios de e-mail
      </label>
      <div className="flex flex-wrap items-center gap-2">
        {value.map((d) => (
          <span
            key={d}
            className="flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
          >
            {d}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remover domínio ${d}`}
                onClick={() => onChange(value.filter((x) => x !== d))}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40"
              >
                <MdClose className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </span>
        ))}
        {value.length === 0 && disabled && (
          <span className="text-xs text-gray-700 dark:text-gray-300">Nenhum domínio cadastrado.</span>
        )}
      </div>
      {!disabled && (
        <input
          id={id}
          value={rascunho}
          onChange={(e) => setRascunho(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Enter dentro do modal não pode submeter nada: só adiciona o chip.
              e.preventDefault();
              adicionar();
            }
          }}
          onBlur={adicionar}
          aria-invalid={!!error}
          aria-describedby={`${id}-ajuda`}
          className={inputCls}
          placeholder="ex.: acme.com.br e Enter"
        />
      )}
      <p id={`${id}-ajuda`} className="text-[11px] text-gray-700 dark:text-gray-300">
        Quem entra com e-mail desses domínios é associado a esta organização.
      </p>
      {error && (
        <p role="alert" className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-300">
          <MdErrorOutline className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};

export default DominiosField;
